import {ReactNode, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {BackCrumb, CtaDock, cn} from '@chekinapp/ui';
import {CaptureDockProvider, PageLayoutHeader, useCaptureDock} from '@chekin/shared';
import {MobileTopBar} from 'components/MobileTopBar';
import {useGuardedNavigate} from 'context/navigationGuard';
import {usePath} from 'hooks';
import {useIsPinnedToTop} from 'hooks/useIsPinnedToTop';
import {PATHS} from 'routes/constants';
import {PAGE_COLUMN, PAGE_COLUMN_FLUID, PAGE_TOP_INSET} from './pageColumn';

type Props = {
  title?: ReactNode;
  children: ReactNode;
  kicker?: string;
  crumb?: string;
  kickerOn?: 'auto' | 'mobile' | 'desktop' | 'both';
  intro?: ReactNode;
  titleAddon?: ReactNode;
  align?: 'start' | 'center';
  backLabel?: string;
  onBack?: () => void;
  hideBack?: boolean;
  hideMobileBack?: boolean;
  isBackConcealed?: boolean;
  rightAddon?: ReactNode;
  titleAction?: ReactNode;
  className?: string;
  mobileTitle?: ReactNode;
  mobileSubtitle?: ReactNode;
  mobileTopBarAddon?: ReactNode;
  mobileTopBarBelow?: ReactNode;
  dock?: ReactNode;
  dockClassName?: string;
  fluid?: boolean;
  fitViewport?: boolean;
  fillViewport?: boolean;
  headerAddon?: ReactNode;
  hideHeaderOnMobile?: boolean;
  busy?: boolean;
  bare?: boolean;
};

export function PageLayout({
  kicker,
  crumb,
  kickerOn = 'auto',
  title,
  intro,
  titleAddon,
  align,
  backLabel,
  onBack,
  hideBack,
  hideMobileBack,
  isBackConcealed,
  rightAddon,
  titleAction,
  className,
  mobileTitle,
  mobileSubtitle,
  mobileTopBarAddon,
  mobileTopBarBelow,
  dock,
  dockClassName,
  fluid,
  fitViewport,
  fillViewport,
  headerAddon,
  hideHeaderOnMobile,
  busy,
  bare,
  children,
}: Props) {
  const {t} = useTranslation();
  const guardedNavigate = useGuardedNavigate();
  const {getMainPath} = usePath();

  const handleBack = onBack ?? (() => guardedNavigate(getMainPath(PATHS.home)));
  const hasMobileTopBar = Boolean(mobileTitle);
  const showBack = !hideBack && !bare;
  const hideKickerOnMobile =
    kickerOn === 'desktop' || (kickerOn === 'auto' && hasMobileTopBar);
  const hideKickerOnDesktop = kickerOn === 'mobile';
  const captureDock = useCaptureDock();
  const hasDockContent = Boolean(dock) || captureDock.isOccupied;
  const crumbCurrent =
    crumb ?? kicker ?? (typeof mobileTitle === 'string' ? mobileTitle : undefined);
  const crumbRef = useRef<HTMLDivElement>(null);
  const isCrumbFloating = useIsPinnedToTop(crumbRef, showBack);

  return (
    <div
      className={cn(
        'flex min-h-[var(--app-dvh,100dvh)] w-full flex-col overflow-x-clip lg:min-h-0',
        bare && 'lg:min-h-[var(--app-vh,100vh)]',
        fitViewport && 'lg:h-[var(--app-dvh,100dvh)] lg:min-h-0',
        fillViewport && 'lg:min-h-[var(--app-dvh,100dvh)]',
      )}
    >
      {hasMobileTopBar && (
        <div className="sticky top-[var(--maintenance-banner-height,0px)] z-20 lg:hidden">
          <MobileTopBar
            busy={busy}
            titleAsHeading={hideHeaderOnMobile}
            title={mobileTitle}
            subtitle={mobileSubtitle}
            onBack={handleBack}
            hideBack={hideMobileBack}
            rightAddon={mobileTopBarAddon}
          />
          {mobileTopBarBelow}
        </div>
      )}
      <div
        className={cn(
          bare
            ? 'flex w-full flex-1 flex-col justify-center px-6 pt-8 lg:py-12'
            : 'mx-auto flex w-full max-w-[640px] flex-1 flex-col px-4 lg:flex-none',
          !bare && ['mt-[14px] lg:mt-0', PAGE_TOP_INSET],
          fitViewport && 'lg:min-h-0 lg:flex-1',
          fillViewport && 'lg:flex-1',
          !bare && (fluid ? PAGE_COLUMN_FLUID : PAGE_COLUMN),
          className,
        )}
      >
        {showBack && (
          <div
            ref={crumbRef}
            data-floating={isCrumbFloating || undefined}
            aria-hidden={isBackConcealed || undefined}
            className={cn(
              'transition-opacity duration-300',
              isBackConcealed && 'invisible opacity-0',
              'kiosko:hidden relative z-10 mb-5 lg:sticky lg:top-0 lg:-my-3 lg:mb-2 lg:py-3',
              'before:pointer-events-none before:absolute before:inset-y-0 before:-inset-x-[100vw] before:-z-10 before:opacity-0 before:transition-opacity before:duration-200',
              'before:bg-[var(--top-bar-bg)] before:[backdrop-filter:blur(var(--top-bar-blur))_saturate(var(--top-bar-saturate))] before:[box-shadow:var(--top-bar-elevated-shadow)]',
              'lg:data-[floating]:before:opacity-100',
              hasMobileTopBar && 'hidden lg:block',
            )}
          >
            <BackCrumb
              aria-label={t('breadcrumb')}
              label={backLabel ?? t('home')}
              current={crumbCurrent}
              onBack={handleBack}
            />
          </div>
        )}
        <div className={cn('kiosko:hidden', bare && 'hidden')}>
          <PageLayoutHeader
            className={cn('mb-5', hideHeaderOnMobile && 'hidden lg:flex')}
            kicker={kicker}
            hideKickerOnMobile={hideKickerOnMobile}
            hideKickerOnDesktop={
              hideKickerOnDesktop || (showBack && Boolean(crumbCurrent))
            }
            title={title}
            intro={intro}
            titleAddon={titleAddon}
            align={align}
            rightAddon={rightAddon}
            titleAction={titleAction}
          >
            {headerAddon}
          </PageLayoutHeader>
        </div>
        <CaptureDockProvider value={captureDock.value}>{children}</CaptureDockProvider>
      </div>
      <CtaDock
        elevated
        className={cn(
          'lg:bg-transparent lg:shadow-none',
          'lg:static lg:mx-auto lg:mt-6 lg:w-full',
          fluid ? PAGE_COLUMN_FLUID : PAGE_COLUMN,
          'lg:bg-none lg:pb-[60px] lg:pt-0',
          'lg:flex-row lg:justify-center',
          !hasDockContent && 'hidden',
          captureDock.className,
          dockClassName,
        )}
      >
        {dock}
        <div ref={captureDock.containerRef} className="contents" />
      </CtaDock>
    </div>
  );
}
